// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface CouponConversion {
  id: string;
  referral_code: string;
  amount_earned: number;
  earned_at: string;
  user_name: string;
  user_email: string;
  user_avatar: string | null;
}

interface CouponConversionsData {
  [couponCode: string]: CouponConversion[];
}

export const useAffiliateCouponConversions = (profileId: string) => {
  const [conversions, setConversions] = useState<CouponConversionsData>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConversions = async () => {
    if (!profileId) return;

    setLoading(true);
    setError(null);

    try {
      console.log('fetchConversions called with profileId:', profileId);
      
      // Get affiliate record first to filter conversions by current user
      const { data: affiliate, error: affiliateError } = await supabase
        .from('affiliates')
        .select('id')
        .eq('user_id', profileId)
        .single();

      console.log('Coupon affiliate query result:', { affiliate, affiliateError, profileId });

      if (affiliateError || !affiliate) {
        console.log('No affiliate found for profile:', profileId, 'Error:', affiliateError);
        setConversions({});
        return;
      }

      // Query conversions for this affiliate only
      const { data: conversionsData, error: fetchError } = await supabase
        .from('affiliate_conversions')
        .select('id, referral_code, amount_earned, earned_at, referred_user_id')
        .eq('affiliate_id', affiliate.id)
        .eq('attribution_method', 'coupon_code')
        .order('earned_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      // Then get user details for each conversion (referred_user_id points to auth.users)
      const userIds = conversionsData?.map(c => c.referred_user_id).filter(Boolean) || [];
      
      let usersData = [];
      if (userIds.length > 0) {
        const { data: users, error: usersError } = await supabase
          .from('profiles')
          .select('user_id, name, email, avatar_url')
          .in('user_id', userIds);
          
        if (usersError) {
          console.error('Error fetching user profiles:', usersError);
        } else {
          usersData = users || [];
        }
      }

      // Group conversions by coupon code
      const groupedConversions: CouponConversionsData = {};
      
      if (conversionsData) {
        conversionsData.forEach((conversion: any) => {
          const couponCode = conversion.referral_code;
          if (!groupedConversions[couponCode]) {
            groupedConversions[couponCode] = [];
          }
          
          // Find matching user profile (match by user_id not profile id)
          const userProfile = usersData.find(user => user.user_id === conversion.referred_user_id);
          
          groupedConversions[couponCode].push({
            id: conversion.id,
            referral_code: conversion.referral_code,
            amount_earned: conversion.amount_earned,
            earned_at: conversion.earned_at,
            user_name: userProfile?.name || 'Unknown User',
            user_email: userProfile?.email || '',
            user_avatar: userProfile?.avatar_url || null
          });
        });
      }

      setConversions(groupedConversions);
    } catch (err) {
      console.error('Error fetching coupon conversions:', err);
      setError('Failed to load coupon usage data');
      toast.error('Failed to load coupon usage data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversions();
  }, [profileId]);

  return {
    conversions,
    loading,
    error,
    refetch: fetchConversions
  };
};